import { useCallback, useEffect } from 'react';

import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { useLocation, useParams } from 'react-router';
import { toast } from 'sonner';

import { callGet } from '@/api/fetcher';
import { useDeskId } from '@/hooks/useDeskId';
import { selectAccountSlug } from '@/redux/session/selectors';
import { Integration, IntercomIntegration } from '@/shared/types/integration';
import { INTERCOM_AUTHORIZATION_URL } from '@/util/constants';

import { useIntegrations } from './useIntegrations';

const createCallbackURL = () => {
  return `${window?.location?.origin}/www/api/integrations/intercom/callback`;
};

export const createOAuthURL = async (
  slug: string,
  integration?: Integration,
  collection_id?: string,
  datasource_name?: string
) => {
  const { desk_id, integration_id } = integration ?? {};

  const client_id = await callGet(
    `/www/api/integrations/intercom/client-id/${collection_id ? 'collection' : 'integration'}`
  );
  const callbackUrl = createCallbackURL();
  const queryParams = new URLSearchParams({
    redirect_uri: callbackUrl,
    client_id,
    state: JSON.stringify({ desk_id, integration_id, slug, collection_id, datasource_name }),
  });

  return `${INTERCOM_AUTHORIZATION_URL}?${queryParams}`;
};

export const useIntercom = () => {
  const { t } = useTranslation();
  const deskId = useDeskId();
  const { integrationId } = useParams();
  const slug = useSelector(selectAccountSlug);
  const loc = useLocation();

  const { integration, updateStatus, updateIntegration } = useIntegrations<IntercomIntegration>(
    deskId,
    integrationId
  );

  const isActive = integration?.active;

  const isUpdated = updateStatus === 'success';
  const isUpdating = updateStatus === 'pending';
  const error = updateStatus === 'error';

  const isConnected = integration?.config?.access_token != null;

  useEffect(() => {
    if (error) {
      toast.error(t('integrations.intercom.errors.generic'));
    }
  }, [error, t]);

  const updateIntercomIntegration = useCallback(
    (i: IntercomIntegration) => {
      updateIntegration(i);
    },
    [updateIntegration]
  );

  const handleOAuthClick = useCallback(async () => {
    const intercomUrl = await createOAuthURL(slug, integration);
    window.open(intercomUrl, '_self');
  }, [slug, integration]);

  useEffect(() => {
    const oauthError = new URLSearchParams(loc.search).get('error');
    if (oauthError) {
      toast.error(t('integrations.intercom.errors.generic'));
    }
  }, [loc.search, t]);

  return {
    integration,
    isActive,
    isUpdating,
    isUpdated,
    isConnected,
    updateIntercomIntegration,
    handleOAuthClick,
  };
};
